// TODO: This file should be deprecated in favor of storing this data in Sanity
import type { CareRecipient } from "~www/types/forms/care-seeker-shallow-lead-form";

import HeartWithPerson from "@homeinstead/icons/HeartWithPerson";
import PeopleWithHeart from "@homeinstead/icons/PeopleWithHeart";
import Person from "@homeinstead/icons/Person";

export type RecipientOptionItem = {
  value: CareRecipient;
  label: string;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
};

export const RecipientOptionsV1: RecipientOptionItem[] = [
  {
    icon: PeopleWithHeart,
    label: "A Parent",
    value: "parent",
  },
  {
    icon: HeartWithPerson,
    label: "My Spouse",
    value: "spouse",
  },
  {
    icon: Person,
    label: "Me",
    value: "me",
  },
  {
    icon: Person,
    label: "Someone Else",
    value: "other",
  },
];

export const RecipientOptions: RecipientOptionItem[] = [
  {
    icon: PeopleWithHeart,
    label: "My parent(s)",
    value: "parent",
  },
  {
    icon: HeartWithPerson,
    label: "My spouse",
    value: "spouse",
  },
  {
    icon: Person,
    label: "Myself",
    value: "me",
  },
  {
    icon: Person,
    label: "Someone else",
    value: "other",
  },
];

export type RecipientType = (typeof RecipientOptions)[number]["value"];

export const CareOptions = [
  [
    "mobilityAssistance",
    "Mobility Assistance",
    <ul
      className="list-inside list-disc font-light text-support-base"
      key="mobilityAssistance"
    >
      <li>
        <span className="relative -left-2">Activities of Daily Living</span>
      </li>
      <li>
        <span className="relative -left-2">Bathing and Grooming</span>
      </li>
      <li>
        <span className="relative -left-2">Meal Prep and Home Help</span>
      </li>
      <li>
        <span className="relative -left-2">Medication reminders</span>
      </li>
      <li>
        <span className="relative -left-2">Companionship</span>
      </li>
      <li>
        <span className="relative -left-2">Toileting and Incontinence</span>
      </li>
      <li>
        <span className="relative -left-2">Transportation</span>
      </li>
    </ul>,
  ],
  [
    "everydayTasks",
    "Everyday Tasks",
    <ul
      className="list-inside list-disc font-light text-support-base"
      key="everydayTasks"
    >
      <li>
        <span className="relative -left-2">Age-related dementia</span>
      </li>
      <li>
        <span className="relative -left-2">{`Parkinson's disease`}</span>
      </li>
      <li>
        <span className="relative -left-2">Heart disease or stroke</span>
      </li>
      <li>
        <span className="relative -left-2">Weight-related issues</span>
      </li>
      <li>
        <span className="relative -left-2">Cancer</span>
      </li>
      <li>
        <span className="relative -left-2">Diabetes</span>
      </li>
      <li>
        <span className="relative -left-2">COPD</span>
      </li>
    </ul>,
  ],
  [
    "memoryCare",
    "Memory care",
    <ul
      className="list-inside list-disc font-light text-support-base"
      key="memoryCare"
    >
      <li>
        <span className="relative -left-2">
          Mild Cognitive Impairment (MCI)
        </span>
      </li>
      <li>
        <span className="relative -left-2">{`Alzheimer's Disease`}</span>
      </li>
      <li>
        <span className="relative -left-2">Vascular Dementia</span>
      </li>
      <li>
        <span className="relative -left-2">Lewy Body Dementia</span>
      </li>
      <li>
        <span className="relative -left-2">Frontotemporal Dementia</span>
      </li>
      <li>
        <span className="relative -left-2">{`Parkinson's Disease`}</span>
      </li>
      <li>
        <span className="relative -left-2">Dementia</span>
      </li>
    </ul>,
  ],
  [
    "other",
    "Other",
    <ul
      className="list-inside list-disc font-light text-support-base"
      key="other"
    >
      <li>
        <span className="relative -left-2">Nurse-Directed Support</span>
      </li>
      <li>
        <span className="relative -left-2">Medication Management</span>
      </li>
      <li>
        <span className="relative -left-2">Glucose Checks</span>
      </li>
      <li>
        <span className="relative -left-2">Wound Care</span>
      </li>
    </ul>,
  ],
] as const;

export type CareOptionType = (typeof CareOptions)[number][0];

export const CareOptionsWithBriefDescriptions = [
  [
    "mobilityAssistance",
    "Daily Living Tasks",
    "Light housekeeping, meal prep, and medication reminders.",
  ],
  ["memoryCare", "Memory Support", "Alzheimer's and other forms of dementia."],
  [
    "everydayTasks",
    "Condition Specific Care",
    "Arthritis, diabetes, hospital dismissal, end of life care, etc.",
  ],
  [
    "other",
    "Other Types of Support",
    "Errands/Transportation, wellness updates, companionship, etc.",
  ],
] as const;

/**
 * Tuple representing a care option: [fieldName, label, helperTextOrElement?, iconElement?]
 * Used by both CareOptions (V1) and CareOptionsWithBriefDescriptions.
 * V1 has React elements at position 2, V2 has strings at position 2.
 */
export type CareOptionTuple = readonly [
  string,
  string,
  (string | React.ReactElement)?,
  React.ReactElement?,
];

/**
 * Get the human-readable label for a care option field name.
 * Used to map field names to labels for tracking events.
 * @param fieldName - The internal field name (e.g., "mobilityAssistance")
 * @param options - The care options array to search in
 * @returns The human-readable label or the original field name if not found
 */
export const getCareOptionLabel = (
  fieldName: string,
  options: readonly (readonly [string, string, ...unknown[]])[],
): string => {
  const found = options.find(([name]) => name === fieldName);
  return found ? found[1] : fieldName;
};

export const UrgencyOptions = [
  { label: "Right away", value: "right-away" },
  { label: "Within a few weeks", value: "within-a-few-weeks" },
  { label: "Just planning ahead", value: "just-planning-ahead" },
  { label: "I'm not sure", value: "not-sure" },
] as const;

export type UrgencyType = (typeof UrgencyOptions)[number]["value"];

/**
 * Tuple of urgency values for use in zod enum schemas.
 * Typed as [string, ...string[]] to satisfy zod's enum requirements.
 */
export const urgencyValues = UrgencyOptions.map((o) => o.value) as [
  UrgencyType,
  ...UrgencyType[],
];

/**
 * Get the human-readable label for an urgency value.
 * Falls back to the raw value if not found.
 */
export const getUrgencyLabel = (value: string): string =>
  UrgencyOptions.find((o) => o.value === value)?.label ?? value;
